import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper";
import { ScalableImage, Typography } from "design_system/src";
import { useMediaQuery } from "@mui/material";
import Tippy from "@tippyjs/react";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";
import styles from "./AlliesOrRecognitions.module.scss";


interface AlliesOrRecognitionsProps {
  type: "allies" | "brands" | "recognitions";
}

interface SwiperItemData {
  name: string;
  image: React.ReactElement;
  link: string;
  modalText?: React.ReactNode;
}

const alliesList: SwiperItemData[] = [
  {
    name: "Bodega Aurrera",
    link: "/bodega-aurrera/financia-tu-moto",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "70px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Aliados/logo_aliado_bodega_aurrera_blanco_xsmall"
      alt="Bodega Aurrera, aliado de Ozon para acceder a motos nuevas a crédito"
    />,
    modalText: <>Accede al <strong>catálogo de Bodega Aurrera</strong> con <strong>financiamiento flexible.</strong> Compra fácil, recibe tu moto en <strong>5 días hábiles</strong> y paga en <strong>semanalidades accesibles.</strong></>,
  },
  {
    name: "Uber",
    link: "/",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Aliados/logo_aliado_uber_blanco_xsmall"
      alt="Por ser repartidor de Uber obtienes 7% de descuento en tu moto financiada"
    />,
    modalText: <>Por ser repartidor de Uber obtienes <strong>7% de descuento en cada cuota</strong> que se retenga mediante la app.</>,
  },
  {
    name: "DiDi Food",
    link: "/didi/financia-tu-moto",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Aliados/logo_aliado_didi_food_blanco"
      alt="Por ser repartidor de DiDiFood obtienes 5% de descuento sobre el total del financiamiento"
    />,
    modalText: <>Por ser repartidor de DiDiFood obtienes <strong>5% de descuento</strong> sobre el <strong>total del financiamiento</strong></>,
  },
  {
    name: "inDrive",
    link: "https://indrive.com/es-mx",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "35px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Aliados/logo_aliado_indrive_descuento"
      alt="inDrive, aliado de Ozon para financiamiento flexible con 5% de descuento si eres socio"
    />,
    modalText: <>Accede a tu moto con <strong>financiamiento flexible</strong> y recibe un <strong>5% de descuento si eres socio. Genera ingresos</strong> como conductor en la nueva modalidad de <strong>viajes en moto de InDrive.</strong></>,
  },
  {
    name: "Walmart",
    link: "/walmart/financia-tu-moto",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "35px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Aliados/logo_aliado_walmart_blanco_xsmall"
      alt="Walmart, aliado de Ozon para acceder a motos nuevas a crédito en 5 días hábiles"
    />,
    modalText: <>Accede al <strong>catálogo de Walmart</strong> con <strong>financiamiento flexible.</strong> Compra fácil, recibe tu moto en <strong>5 días hábiles</strong> y paga en <strong>semanalidades accesibles.</strong></>,
  },
];

const brandsList: SwiperItemData[] = [
  {
    name: "Bajaj",
    link: "/catalogo?brand=BAJAJ",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_bajaj_blanco"
      alt="Logo de la marca Bajaj – motocicletas a crédito en Ozon"
    />,
  },
  {
    name: "Pulsar",
    link: "/catalogo?model=PULSAR",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_pulsar_blanco"
      alt="Logo Pulsar"
    />,
  },
  {
    name: "TVS",
    link: "/catalogo?brand=TVS",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_tvs_blanco"
      alt="Logo TVS"
    />,
  },
  {
    name: "Vento",
    link: "/catalogo?brand=VENTO",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_vento_blanco"
      alt="Logo de la marca Vento – motocicletas en venta y financiamiento en Ozon"
    />,
  },
  {
    name: "Italika",
    link: "/catalogo?brand=ITALIKA",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_Italika_blanco"
      alt="Logo de la marca Italika – motocicletas disponibles en Ozon"
    />,
  },
  {
    name: "Victory",
    link: "/catalogo?brand=VICTORY",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Marcas/logo_marca_victory_blanco"
      alt="Logo de la marca Victory – motocicletas nuevas y seminuevas en Ozon"
    />,
  },
];

const recognitionsList: SwiperItemData[] = [
  {
    name: "Addem",
    link: "https://www.addemcapital.mx/historias/jotfubigw9h55ix26oee7ko23y0g78",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Reconocimientos/logo_addem_capital_blanco"
      alt="Addem Capital"
    />,
  },
  {
    name: "México tech week",
    link: "https://techla.pro/2024/10/23/en-el-marco-de-la-mexico-tech-week-2024-se-presenta-el-mexico-early-stage-100-celebrando-la-excelencia-de-las-startups-mexicanas/",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Reconocimientos/logo_mexico_tech_week_blanco"
      alt="México tech week"
    />,
  },
  {
    name: "Palenca",
    link: "https://blog.palenca.com/ozon-revolucionando-el-financiamiento-de-motos/",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Reconocimientos/logo_palenca_blanco"
      alt="Palenca"
    />,
  },
  {
    name: "Forbes",
    link: "https://forbes.com.mx/ozon-el-kavak-de-las-motos-acelera-su-negocio-en-mexico/",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Reconocimientos/logo_forbes_blanco"
      alt="Forbes"
    />,
  },
  {
    name: "Forbes",
    link: "https://forbes.co/2021/12/21/actualidad/ozon-entregara-creditos-a-repartidores-de-plataformas-para-que-compren-sus-propias-motos",
    image: <ScalableImage
      desktopWidth={5}
      mobileWidth={5}
      style={{ width: "100%", height: "30px", objectFit: "contain" }}
      src="https://ik.imagekit.io/ozon/Sobre%20Nosotros/Reconocimientos/logo_forbes_blanco"
      alt="Forbes"
    />,
  },
];

const AlliesOrRecognitions: React.FC<AlliesOrRecognitionsProps> = ({ type }) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const getTitle = () => {
    if (type === "allies") return "Nuestros aliados";
    if (type === "brands") return "Nuestras marcas";
    return "Reconocimientos";
  };

  const getList = (): SwiperItemData[] => {
    if (type === "allies") return alliesList;
    if (type === "brands") return brandsList;
    return recognitionsList;
  };

  const renderTooltip = (text: React.ReactNode) => (
    <div className="bg_neutral_0 br_sm p_y_xl p_x_sm">
      <Typography scale="small" weight="400" className="text_neutral_800 text_center">{text}</Typography>
    </div>
  );

  const renderModal = (html: React.ReactNode, link: string) => {
    Swal.fire({
      html: ReactDOMServer.renderToString(
        <div className="bg_neutral_0 br_sm p_y_xl p_x_sm pos_relative">
          <div className="text_neutral_800 text_center m_y_sm">{html}</div>
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="dso_btn_outline dso_btn_color_primary  dso_btn_small br_md text_decoration_none display_block m_b_sm"
          >
            Visitar sitio web
          </a>
        </div>
      ),
      showConfirmButton: false,
      showCloseButton: true,
      closeButtonHtml: ReactDOMServer.renderToString(
        <span style={{ fontSize: "18px", fontWeight: "bold" }}>✕</span>
      ),
      customClass: {
        container: "text_neutral_700",
        closeButton: "pos_absolute bg_transparent border_none",
      },
    });
  };

  return (
    <div className="border_solid border_1 border_neutral_0 br_sm p_y_xl m_y_lg p_x_lg pos_relative">
      <div className="pos_absolute w_100_per" style={{ top: "-15px", left: 0, right: 0, margin: "auto" }}>
        <div className="dso_container w_100_per display_flex flex_justify_center">
          <Typography
            weight="400"
            scale="large"
            className="bg_primary_300 m_none w_fit p_x_xl text_center"
            component="p"
          >{getTitle()}</Typography>
        </div>
      </div>

      {!isDesktop && <div>
        <div className={styles.infinitveMobileScroll}>
          <div className={styles.infinitveMobileScrollDiv}>
            {getList().map(({ name, image, link, modalText }, index) => (
              <div
                key={`${type}-${index}-mobile-1`}
                className={styles.infinitveMobileScrollImg}
              >
                {modalText && (
                  <div className="w_100_per h_100_per flex_center" onClick={() => renderModal(modalText, link)}>
                    {image}
                  </div>
                )}
                {!modalText && (
                  <a href={link} className="w_100_per h_100_per flex_center" target="_blank" rel="noreferrer">
                    {image}
                  </a>
                )}
              </div>
            ))}
          </div>
          <div className={styles.infinitveMobileScrollDiv}>
            {getList().map(({ name, image, link, modalText }, index) => (
              <div
                key={`${type}-${index}-mobile-2`}
                className={styles.infinitveMobileScrollImg}
              >
                {modalText && (
                  <div className="w_100_per h_100_per flex_center" onClick={() => renderModal(modalText, link)}>
                    {image}
                  </div>
                )}
                {!modalText && (
                  <a href={link} className="w_100_per h_100_per flex_center" target="_blank" rel="noreferrer">
                    {image}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>}


      {isDesktop && <div>
        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={5}
          centeredSlides={false}
        >
          {getList().map(({ name, image, link, modalText }, index) => (
            <SwiperSlide
              key={`${type}-${index}-desktop`}
              style={{ background: "transparent", alignSelf: "center" }}
            >
              {modalText && (
                <Tippy content={renderTooltip(modalText)}>
                  <a href={link} className="w_100_per h_100_per" target="_blank" rel="noreferrer">
                    {image}
                  </a>
                </Tippy>
              )}
              {!modalText && (
                <a href={link} className="w_100_per h_100_per" target="_blank" rel="noreferrer">
                  {image}
                </a>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>}
    </div>
  );
};

export default AlliesOrRecognitions;